import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useUser } from "../contexts/UserContext";
import {
  getAdminMemberBilling,
  getAdminMembers,
  requestAdminDodoRefund,
} from "./firebaseFunctions";
import { AdminMemberSummaryItem, BillingTransactionItem } from "../types";
import styles from "./AdminMembersPage.module.css";

const ADMIN_LEVEL_MAX = 1;
const MEMBER_PAGE_SIZE = 50;
const BILLING_PAGE_SIZE = 20;

type EventFilterValue = "all" | "payment" | "refund" | "failed" | "subscription" | "other";

const formatNumber = (value: number | null | undefined): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "-";
  }
  return value.toLocaleString("ko-KR");
};

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) {
    return "-";
  }
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) {
    return "-";
  }
  return new Intl.DateTimeFormat("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const toDateInputValue = (iso: string | null | undefined): string => {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isSubscriptionProductCode = (productCode: string | null | undefined): boolean =>
  productCode === "creator_300_monthly" ||
  productCode === "creator_monthly" ||
  productCode === "creator_500_monthly" ||
  productCode === "creator_1000_monthly";

const isPaymentSuccessEvent = (eventType: string | null | undefined): boolean => {
  const value = String(eventType ?? "").toLowerCase();
  return value === "payment.succeeded" || value === "subscription_payment_success";
};

const isRefundEvent = (eventType: string | null | undefined): boolean => {
  const value = String(eventType ?? "").toLowerCase();
  return value === "refund.succeeded" || value === "order_refunded" || value.includes("refund");
};

const isFailureEvent = (eventType: string | null | undefined): boolean => {
  const value = String(eventType ?? "").toLowerCase();
  return value === "payment.failed" || value.includes("failed");
};

const isRefundedStatus = (status: string | null | undefined): boolean =>
  String(status ?? "").toLowerCase() === "refunded";

const getEventLabel = (eventType: string | null | undefined): string => {
  const value = String(eventType ?? "").toLowerCase();
  switch (value) {
    case "payment.succeeded":
      return "결제 성공";
    case "subscription_payment_success":
      return "구독 결제 성공";
    case "refund.succeeded":
      return "환불 완료";
    case "order_refunded":
      return "주문 환불";
    case "payment.failed":
      return "결제 실패";
    case "order_created":
      return "주문 생성";
    default:
      return eventType ?? "-";
  }
};

const getStatusLabel = (status: string | null | undefined): string => {
  const value = String(status ?? "").toLowerCase();
  switch (value) {
    case "paid":
      return "결제완료";
    case "refunded":
      return "환불완료";
    case "failed":
      return "실패";
    case "processing":
      return "처리중";
    case "canceled":
      return "취소";
    default:
      return status ?? "-";
  }
};

const matchesEventFilter = (tx: BillingTransactionItem, filter: EventFilterValue): boolean => {
  if (filter === "all") {
    return true;
  }
  if (filter === "payment") {
    return isPaymentSuccessEvent(tx.eventType);
  }
  if (filter === "refund") {
    return isRefundEvent(tx.eventType) || isRefundedStatus(tx.status);
  }
  if (filter === "failed") {
    return isFailureEvent(tx.eventType);
  }
  if (filter === "subscription") {
    const product = String(tx.productCode ?? "").toLowerCase();
    return product.includes("monthly") || product.includes("subscription");
  }
  const raw = String(tx.eventType ?? "").toLowerCase();
  return !isPaymentSuccessEvent(raw) && !isRefundEvent(raw) && !isFailureEvent(raw);
};

const AdminMembersPage: React.FC = () => {
  const userContext = useUser();
  const userDetails = userContext?.userDetails;
  const isAdmin =
    userDetails?.id !== "Guest" &&
    typeof userDetails?.userLevel === "number" &&
    userDetails.userLevel <= ADMIN_LEVEL_MAX;

  const [keywordInput, setKeywordInput] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");

  const [members, setMembers] = useState<AdminMemberSummaryItem[]>([]);
  const [memberPageIndex, setMemberPageIndex] = useState(0);
  const [memberPageCursors, setMemberPageCursors] = useState<Array<string | null>>([
    null,
  ]);
  const [membersHasMore, setMembersHasMore] = useState(false);
  const [membersLoading, setMembersLoading] = useState(false);

  const [selectedMember, setSelectedMember] = useState<AdminMemberSummaryItem | null>(
    null
  );
  const [transactions, setTransactions] = useState<BillingTransactionItem[]>([]);
  const [billingCursor, setBillingCursor] = useState<string | null>(null);
  const [billingHasMore, setBillingHasMore] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [refundProcessingTxId, setRefundProcessingTxId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const [eventFilter, setEventFilter] = useState<EventFilterValue>("all");
  const [productFilter, setProductFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadMembersPage = useCallback(
    async (page: number, cursor: string | null) => {
      if (!isAdmin) {
        return;
      }

      setMembersLoading(true);
      setErrorMessage("");

      try {
        const response = await getAdminMembers(searchKeyword, MEMBER_PAGE_SIZE, cursor);

        setMembers(response.members);
        setMembersHasMore(response.hasMore);
        setMemberPageIndex(page);

        setMemberPageCursors((prev) => {
          const next = prev.slice(0, Math.max(prev.length, page + 1));
          next[page] = cursor;
          if (response.hasMore && response.nextCursor) {
            next[page + 1] = response.nextCursor;
          } else {
            next.splice(page + 1);
          }
          if (next[0] !== null) {
            next[0] = null;
          }
          return next;
        });

        setSelectedMember((prev) => {
          if (response.members.length === 0) {
            return null;
          }
          if (!prev) {
            return response.members[0];
          }
          return (
            response.members.find((member) => member.userId === prev.userId) ??
            response.members[0]
          );
        });
      } catch (error: any) {
        setErrorMessage(error?.message ?? "회원 목록을 불러오지 못했습니다.");
      } finally {
        setMembersLoading(false);
      }
    },
    [isAdmin, searchKeyword]
  );

  const loadBilling = useCallback(
    async (
      member: AdminMemberSummaryItem,
      cursor: string | null,
      append: boolean
    ) => {
      setBillingLoading(true);
      setErrorMessage("");
      try {
        const response = await getAdminMemberBilling(member.userId, BILLING_PAGE_SIZE, cursor);
        setTransactions((prev) =>
          append ? [...prev, ...response.transactions] : response.transactions
        );
        setBillingCursor(response.nextCursor);
        setBillingHasMore(response.hasMore);
      } catch (error: any) {
        setErrorMessage(error?.message ?? "결제 내역을 불러오지 못했습니다.");
      } finally {
        setBillingLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    setMembers([]);
    setMemberPageIndex(0);
    setMemberPageCursors([null]);
    setMembersHasMore(false);
    setSelectedMember(null);
    setTransactions([]);
    setBillingCursor(null);
    setBillingHasMore(false);

    if (isAdmin) {
      void loadMembersPage(0, null);
    }
  }, [isAdmin, searchKeyword, loadMembersPage]);

  useEffect(() => {
    if (!selectedMember) {
      setTransactions([]);
      setBillingCursor(null);
      setBillingHasMore(false);
      return;
    }
    setTransactions([]);
    setBillingCursor(null);
    setBillingHasMore(false);
    setEventFilter("all");
    setProductFilter("all");
    setDateFrom("");
    setDateTo("");
    void loadBilling(selectedMember, null, false);
  }, [selectedMember, loadBilling]);

  const productOptions = useMemo(() => {
    const set = new Set<string>();
    for (const tx of transactions) {
      const code = String(tx.productCode ?? "").trim();
      if (code.length > 0) {
        set.add(code);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    return transactions.filter((tx) => {
      if (!matchesEventFilter(tx, eventFilter)) {
        return false;
      }
      if (productFilter !== "all" && tx.productCode !== productFilter) {
        return false;
      }

      const dateValue = toDateInputValue(tx.createdAt);
      if (dateFrom && dateValue && dateValue < dateFrom) {
        return false;
      }
      if (dateTo && dateValue && dateValue > dateTo) {
        return false;
      }

      return true;
    });
  }, [transactions, eventFilter, productFilter, dateFrom, dateTo]);

  const handleRefundClick = useCallback(
    async (tx: BillingTransactionItem) => {
      if (!selectedMember || refundProcessingTxId) {
        return;
      }

      setRefundProcessingTxId(tx.transactionId);
      setErrorMessage("");
      try {
        const preview = await requestAdminDodoRefund(
          selectedMember.userId,
          tx.transactionId,
          true
        );
        const previewAmountText = Number(preview.refundAmountUsd).toFixed(2);
        const previewKindLabel = preview.kind === "full" ? "전액" : "부분";
        const ok = window.confirm(
          `이 결제건에 대해 환불을 요청하시겠습니까?\n예상 환불금액: ${previewAmountText} USD (${previewKindLabel})`
        );
        if (!ok) {
          return;
        }

        const response = await requestAdminDodoRefund(
          selectedMember.userId,
          tx.transactionId
        );
        const deducted = Math.max(0, Number(response.creditsToRevoke ?? 0));
        if (deducted > 0) {
          setSelectedMember((prev) => {
            if (!prev || prev.userId !== selectedMember.userId) {
              return prev;
            }
            return {
              ...prev,
              crd: Math.max(0, prev.crd - deducted),
            };
          });
          setMembers((prev) =>
            prev.map((member) =>
              member.userId === selectedMember.userId ?
                { ...member, crd: Math.max(0, member.crd - deducted) } :
                member
            )
          );
        }
        const amountText = Number(response.refundAmountUsd).toFixed(2);
        window.alert(
          `환불 요청 완료: ${response.kind} / ${amountText} USD / 차감 ${formatNumber(
            response.creditsToRevoke
          )} CRD`
        );
        await loadBilling(selectedMember, null, false);
      } catch (error: any) {
        const message = error?.message ?? "환불 요청에 실패했습니다.";
        setErrorMessage(message);
        window.alert(message);
      } finally {
        setRefundProcessingTxId(null);
      }
    },
    [loadBilling, refundProcessingTxId, selectedMember]
  );

  const summaryText = useMemo(() => {
    if (!selectedMember) {
      return "회원을 선택하세요.";
    }
    return `${selectedMember.userId} / 레벨 ${selectedMember.userLevel} / CRD ${formatNumber(
      selectedMember.crd
    )}`;
  }, [selectedMember]);

  const canGoPrevPage = !membersLoading && memberPageIndex > 0;
  const nextPageCursor = memberPageCursors[memberPageIndex + 1] ?? null;
  const canGoNextPage = !membersLoading && membersHasMore && nextPageCursor !== null;

  if (!isAdmin) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>회원관리</h1>
        <p className={styles.blocked}>관리자 권한이 필요합니다.</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>회원관리</h1>

      <div className={styles.searchRow}>
        <input
          className={styles.searchInput}
          placeholder="이메일 검색"
          value={keywordInput}
          onChange={(event) => setKeywordInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              setSearchKeyword(keywordInput.trim().toLowerCase());
            }
          }}
        />
        <button
          type="button"
          className={styles.actionButton}
          onClick={() => setSearchKeyword(keywordInput.trim().toLowerCase())}
        >
          검색
        </button>
      </div>

      {errorMessage.length > 0 && <p className={styles.error}>{errorMessage}</p>}

      <div className={styles.layout}>
        <section className={styles.membersPanel}>
          <h2 className={styles.sectionTitle}>회원 목록</h2>

          <div className={styles.pageRow}>
            <button
              type="button"
              className={styles.pageButton}
              disabled={!canGoPrevPage}
              onClick={() => {
                const prevPage = memberPageIndex - 1;
                const prevCursor = memberPageCursors[prevPage] ?? null;
                void loadMembersPage(prevPage, prevCursor);
              }}
            >
              이전
            </button>
            <span className={styles.pageText}>
              {memberPageIndex + 1} 페이지 · {MEMBER_PAGE_SIZE}명
            </span>
            <button
              type="button"
              className={styles.pageButton}
              disabled={!canGoNextPage}
              onClick={() => {
                if (!nextPageCursor) {
                  return;
                }
                void loadMembersPage(memberPageIndex + 1, nextPageCursor);
              }}
            >
              다음
            </button>
          </div>

          <ul className={styles.memberList}>
            {members.map((member) => (
              <li key={member.userId}>
                <button
                  type="button"
                  className={`${styles.memberButton} ${
                    selectedMember?.userId === member.userId ? styles.memberButtonActive : ""
                  }`}
                  onClick={() => setSelectedMember(member)}
                >
                  <span>{member.userId}</span>
                  <span>CRD {formatNumber(member.crd)}</span>
                </button>
              </li>
            ))}
          </ul>
          {membersLoading && <p className={styles.hint}>불러오는 중...</p>}
          {!membersLoading && members.length === 0 && (
            <p className={styles.hint}>회원이 없습니다.</p>
          )}
        </section>

        <section className={styles.detailPanel}>
          <h2 className={styles.sectionTitle}>회원 상세</h2>
          <p className={styles.summary}>{summaryText}</p>

          <div className={styles.filterRow}>
            <select
              className={styles.filterControl}
              value={eventFilter}
              onChange={(event) => setEventFilter(event.target.value as EventFilterValue)}
            >
              <option value="all">전체 이벤트</option>
              <option value="payment">결제</option>
              <option value="refund">환불</option>
              <option value="failed">실패</option>
              <option value="subscription">구독</option>
              <option value="other">기타</option>
            </select>

            <select
              className={styles.filterControl}
              value={productFilter}
              onChange={(event) => setProductFilter(event.target.value)}
            >
              <option value="all">전체 상품</option>
              {productOptions.map((productCode) => (
                <option key={productCode} value={productCode}>
                  {productCode}
                </option>
              ))}
            </select>

            <input
              type="date"
              className={styles.filterControl}
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />

            <input
              type="date"
              className={styles.filterControl}
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>일시</th>
                  <th>이벤트</th>
                  <th>상품</th>
                  <th>상태</th>
                  <th>지급</th>
                  <th>회수</th>
                  <th>구독 사용 CRD</th>
                  <th>구독 잔여 CRD</th>
                  <th>전액 환불 가능</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx) => {
                  const grantedRaw = Number(tx.creditGranted ?? 0);
                  const revokedRaw = Number(tx.creditRevoked ?? 0);
                  const paymentEvent = isPaymentSuccessEvent(tx.eventType);
                  const refundEvent = isRefundEvent(tx.eventType);
                  const revokedForCalc = Math.max(0, revokedRaw);
                  const refundedCredits = Math.max(revokedForCalc, Math.max(0, grantedRaw));
                  let displayGrantedCell = "-";
                  let displayRevokedCell = "-";
                  if (paymentEvent) {
                    displayGrantedCell = formatNumber(Math.max(0, grantedRaw));
                    displayRevokedCell = "-";
                  } else if (refundEvent) {
                    displayGrantedCell = "-";
                    displayRevokedCell = refundedCredits > 0 ? formatNumber(refundedCredits) : "-";
                  }
                  const isSubscriptionProduct = isSubscriptionProductCode(tx.productCode);

                  let remainingSubValue: string;
                  let usedSubValue: string;
                  let subUsedValueForRefundCheck = 0;
                  let refundCellText = "-";
                  let canClickRefund = false;
                  if (!isSubscriptionProduct) {
                    remainingSubValue = "-";
                    usedSubValue = "-";
                  } else {
                    const subGranted = Math.max(0, grantedRaw);
                    const subRemaining = isRefundedStatus(tx.status)
                      ? 0
                      : Math.max(0, Number(tx.remainingSubscriptionCRD ?? 0));
                    // "구독 사용 CRD"는 환불 회수분을 제외한 실제 소비량만 표시한다.
                    const subUsed = Math.max(
                      0,
                      subGranted - subRemaining - revokedForCalc
                    );
                    remainingSubValue = formatNumber(subRemaining);
                    usedSubValue = formatNumber(subUsed);
                    subUsedValueForRefundCheck = subUsed;
                  }

                  if (refundEvent) {
                    remainingSubValue = "-";
                    usedSubValue = "-";
                  }
                  if (!paymentEvent) {
                    refundCellText = "-";
                  } else if (isRefundedStatus(tx.status)) {
                    refundCellText = "Refunded";
                  } else if (isSubscriptionProduct) {
                    if (subUsedValueForRefundCheck > 0) {
                      refundCellText = "Used subscription CRD (blocked)";
                    } else {
                      refundCellText = "Full refund";
                      canClickRefund = true;
                    }
                  } else {
                    const purchaseRemaining = Math.max(
                      0,
                      Number(tx.remainingPurchasedCRD ?? 0)
                    );
                    const granted = Math.max(0, grantedRaw);
                    if (purchaseRemaining >= granted) {
                      refundCellText = "Full refund";
                      canClickRefund = true;
                    } else {
                      refundCellText = "Partial refund";
                      canClickRefund = true;
                    }
                  }
                  return (
                    <tr key={tx.transactionId}>
                      <td>{formatDateTime(tx.createdAt)}</td>
                      <td>{getEventLabel(tx.eventType)}</td>
                      <td>{tx.productCode ?? "-"}</td>
                      <td>{getStatusLabel(tx.status)}</td>
                      <td>{displayGrantedCell}</td>
                      <td>{displayRevokedCell}</td>
                      <td>{usedSubValue}</td>
                      <td>{remainingSubValue}</td>
                      <td>
                        {canClickRefund ? (
                          <button
                            type="button"
                            className={styles.refundButton}
                            onClick={() => void handleRefundClick(tx)}
                            disabled={Boolean(refundProcessingTxId)}
                          >
                            {refundProcessingTxId === tx.transactionId ?
                              "..." :
                              "Refund"}
                          </button>
                        ) : (
                          <span>{refundCellText}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {billingLoading && <p className={styles.hint}>결제 내역 불러오는 중...</p>}
          {!billingLoading && selectedMember && filteredTransactions.length === 0 && (
            <p className={styles.hint}>조건에 맞는 결제 내역이 없습니다.</p>
          )}
          {!billingLoading && selectedMember && billingHasMore && (
            <button
              type="button"
              className={styles.moreButton}
              onClick={() =>
                selectedMember && void loadBilling(selectedMember, billingCursor, true)
              }
            >
              결제 내역 더보기
            </button>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminMembersPage;
